import React, { useState, useRef, useEffect, useCallback } from 'react';

interface EditableImageProps {
    src: string;
    alt: string;
    className?: string; // Container classes (w-full h-full relative overflow-hidden)
    imageClassName?: string; // Img specific classes (object-cover)
    editable?: boolean;
    initialScale?: number;
    initialPos?: { x: number; y: number }; // Percentage 0-100 (50 is center)
    onSave?: (scale: number, pos: { x: number; y: number }) => void;
}

const EditableImage: React.FC<EditableImageProps> = ({
    src, 
    alt, 
    className = 'w-full h-full relative overflow-hidden', 
    imageClassName = 'object-cover w-full h-full', 
    editable = false, 
    initialScale = 1, 
    initialPos = { x: 50, y: 50 }, 
    onSave
}) => {
    const [scale, setScale] = useState(initialScale);
    const [pos, setPos] = useState(initialPos);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync from props
    useEffect(() => {
        if (initialScale !== undefined) setScale(initialScale);
        if (initialPos !== undefined) setPos(initialPos);
    }, [initialScale, initialPos, src]);

    // Handle Wheel Zooming via native event to prevent default scrolling
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !editable) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault(); // Stop page scroll
            const delta = e.deltaY * -0.002;
            let newScale = Math.min(Math.max(1, scale + delta), 4); // clamp 1x to 4x
            
            // To be precise, zoom should center on mouse, but simple scale is fine for now
            setScale(newScale);
            
            // Auto save after wheel end
            if (onSave) {
                // simple debounce for save
                clearTimeout((window as any).wheelSaveTimeout);
                (window as any).wheelSaveTimeout = setTimeout(() => {
                    onSave(newScale, pos);
                }, 500);
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        // The event listener uses the current `scale` and `pos`, 
        // so it must be added/removed when they change if we don't use refs.
        // A better way is using a ref for current state to avoid re-binding.
        return () => container.removeEventListener('wheel', handleWheel);
    }, [editable, scale, pos, onSave]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!editable) return;
        e.preventDefault();
        setIsDragging(true);
    };

    const handleWindowMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !editable || !containerRef.current) return;
        
        const rect = containerRef.current.getBoundingClientRect();
        // calculate delta in percentages. 
        // If image is object-cover, movementX moves the image physically, meaning the framing shifts.
        // dragging right (positive movementX) exposes the left edge, decreasing object-position X.
        const deltaXPercent = (e.movementX / rect.width) * 100 / scale;
        const deltaYPercent = (e.movementY / rect.height) * 100 / scale;

        setPos(prev => ({
            x: Math.min(Math.max(0, prev.x - deltaXPercent), 100),
            y: Math.min(Math.max(0, prev.y - deltaYPercent), 100)
        }));
    }, [isDragging, editable, scale]);

    const handleWindowMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            if (onSave) onSave(scale, pos);
        }
    }, [isDragging, scale, pos, onSave]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleWindowMouseMove);
            window.addEventListener('mouseup', handleWindowMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, [isDragging, handleWindowMouseMove, handleWindowMouseUp]);

    return (
        <div 
            ref={containerRef}
            className={`${className} ${editable ? 'cursor-move' : ''}`}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <img 
                src={src} 
                alt={alt} 
                className={`${imageClassName} transition-transform ease-out`}
                style={{ 
                    transform: `scale(${scale})`, 
                    objectPosition: `${pos.x}% ${pos.y}%`,
                    // We remove transition during drag for 0-latency feel
                    transitionDuration: isDragging ? '0ms' : '200ms'
                }}
            />
            
            {/* Editor Overlay Hint */}
            {editable && isHovered && !isDragging && (
                <div className="absolute top-4 right-4 bg-sky-600/90 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg backdrop-blur flex gap-2 items-center pointer-events-none animate-in fade-in">
                    <span>✥</span> Ziehen & Scrollen
                </div>
            )}
        </div>
    );
};

export default EditableImage;
